import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { decaPdfPath, descargaUrl, firmarUrl } from "@/lib/publicUrls";
import { Field, PageHeader, KV, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { FileText, Pencil, Ban, ChevronLeft, Truck, User, ShieldCheck, Clock, FilePlus2, Plus, Trash2 } from "lucide-react";
import { esCpIntl, esDireccionCompleta, esIdFiscal } from "@/lib/validacionEs";
import EnviarDecaEmail, { BotonEnviarEmail } from "@/components/EnviarDecaEmail";
import QrDisplay from "@/components/QrDisplay";
import { toast } from "sonner";
import DecaNotes from "@/components/DecaNotes";
import { EnviosSortableTable } from "@/components/SortableTh";
import ConductorSelect from "@/components/ConductorSelect";
import MatriculaSelect from "@/components/MatriculaSelect";
import { isPublicDownloadActive } from "@/lib/offline";
import { fmtFecha, fmtFechaHora } from "@/lib/fechas";
import DatePicker from "@/components/DatePicker";
import { fmtAlbaranNro, fmtNumeroDeca, palabraEnvio } from "@/lib/textos";
import { LEN } from "@/lib/limites";
import { useAuth } from "@/auth/AuthContext";
import DestinoClienteSelect, { fmtDomicilio } from "@/components/DestinoClienteSelect";

const MOTIVO_MIN = 10;
const T_MOTIVO = "El motivo es obligatorio (m" + String.fromCharCode(0xed) + "nimo " + MOTIVO_MIN + " caracteres)";
const T_MOTIVO_PH = "M" + String.fromCharCode(0xed) + "nimo " + MOTIVO_MIN + " caracteres";
const A = String.fromCharCode(0xe1);
const E = String.fromCharCode(0xe9);
const I = String.fromCharCode(0xed);
const O = String.fromCharCode(0xf3);
const U = String.fromCharCode(0xfa);
const N = String.fromCharCode(0xf1);

function emptyDest() {
  return { razon_social: "", nif: "", domicilio: "", municipio: "", cp: "", provincia: "", email: "" };
}

function envioDesdeApi(e) {
  const dest = e.destinatario || {};
  return {
    key: e.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    origen: e.origen || "",
    destinatario: {
      razon_social: dest.razon_social || e.destinatario_nombre || "",
      nif: dest.nif || e.destinatario_nif || "",
      domicilio: dest.domicilio || "",
      municipio: dest.municipio || "",
      cp: dest.cp || "",
      provincia: dest.provincia || "",
      email: dest.email || "",
    },
    destino: e.destino || "",
    naturaleza: e.naturaleza || "",
    bultos: e.bultos == null ? "" : String(e.bultos),
    peso_bruto: e.peso_bruto == null ? "" : String(e.peso_bruto),
    albaran_ejercicio: e.albaran_ejercicio == null ? String(new Date().getFullYear()) : String(e.albaran_ejercicio),
    albaran_serie: e.albaran_serie || "",
    albaran_numero: e.albaran_numero == null ? "" : String(e.albaran_numero),
  };
}

function emptyEnvioEdit(naturaleza, bultos) {
  return {
    key: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    origen: "",
    destinatario: emptyDest(),
    destino: "",
    naturaleza: naturaleza || ("Mercanc" + I + "a general"),
    bultos: bultos || "",
    peso_bruto: "",
    albaran_ejercicio: String(new Date().getFullYear()),
    albaran_serie: "",
    albaran_numero: "",
  };
}

function ExpiryCountdown({ until, estado }) {
  if (!until) return null;
  const target = new Date(until);
  const now = new Date();
  const diffMs = target - now;
  const days = Math.ceil(diffMs / 86400000);
  const hours = Math.ceil((diffMs % 86400000) / 3600000);
  if (estado === "anulado") {
    return <span className="status-badge status-anulado" data-testid="expiry-badge"><Clock className="h-3 w-3"/> Descarga desactivada</span>;
  }
  if (estado === "borrador") {
    return <span className="status-badge status-borrador" data-testid="expiry-badge"><Clock className="h-3 w-3"/> Sin descarga p{String.fromCharCode(0xfa)}blica</span>;
  }
  if (diffMs <= 0) {
    return <span className="status-badge status-anulado" data-testid="expiry-badge"><Clock className="h-3 w-3"/> Descarga caducada</span>;
  }
  let cls = "status-generado";
  if (days <= 2) cls = "status-anulado";
  else if (days <= 5) cls = "status-modificado";
  const label = days > 0 ? `${days} día${days === 1 ? "" : "s"}` : `${hours}h`;
  return (
    <span className={`status-badge ${cls}`} data-testid="expiry-badge" title={`Descarga p\u00fablica desactivada el ${fmtFechaHora(until)}`}>
      <Clock className="h-3 w-3"/> Caduca en {label}
    </span>
  );
}

export default function DecaDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [modOpen, setModOpen] = useState(false);
  const [mod, setMod] = useState({ vehiculo: "", remolque: "", conductor: "", precio: "", autorizacion: "", observaciones: "", motivo: "", transportista_nif: "", fecha_descarga: "", cargador_nif: "", orig_id: "cargador", origen: "" });
  const [transportistas, setTransportistas] = useState([]);
  const [cargadores, setCargadores] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [anularMotivo, setAnularMotivo] = useState("");
  const [editEnvios, setEditEnvios] = useState([]);
  const [enviosBusy, setEnviosBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/api/v1/decas/${id}`);
      setData(data);
      setEditEnvios((data.envios || []).map(envioDesdeApi));
      const mats = data.deca.matriculas || [];
      setMod({
        vehiculo: mats[0] || "",
        remolque: mats[1] || "",
        conductor: data.deca.conductor || "",
        precio: data.deca.precio_transporte != null && data.deca.precio_transporte !== "" ? String(data.deca.precio_transporte) : "",
        autorizacion: String(data.deca.autorizacion_especial || "").slice(0, LEN.autorizacion),
        observaciones: String(data.deca.observaciones || "").slice(0, LEN.observaciones),
        motivo: "",
        transportista_nif: data.deca.transportista_nif || data.deca.transportista_snapshot?.nif || "",
        fecha_descarga: String(data.deca.fecha_descarga || "").slice(0, 10),
        cargador_nif: data.deca.cargador_nif || data.deca.cargador_snapshot?.nif || "",
        orig_id: "cargador",
        origen: (data.envios && data.envios[0]?.origen) || fmtDomicilio(data.deca.cargador_snapshot) || "",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  useEffect(() => {
    api.get("/api/v1/decas/manual/opciones")
      .then(({ data: cats }) => {
        setTransportistas(cats.transportistas || []);
        setCargadores(cats.cargadores || []);
      })
      .catch(() => {});
  }, []);

  const esBorrador = data?.deca?.estado === "borrador";
  const traElegido = transportistas.find((t) => (t.nif || "").toUpperCase() === (mod.transportista_nif || "").toUpperCase())
    || (mod.transportista_nif ? {
      nif: mod.transportista_nif,
      razon_social: data?.deca?.transportista_snapshot?.razon_social || "",
    } : null);
  const carSnap = data?.deca?.cargador_snapshot || {};
  const carElegido = cargadores.find((c) => (c.nif || "").toUpperCase() === (mod.cargador_nif || "").toUpperCase())
    || (mod.cargador_nif ? {
      nif: mod.cargador_nif,
      razon_social: carSnap.razon_social || "",
      domicilio: carSnap.domicilio || "",
      municipio: carSnap.municipio || "",
      cp: carSnap.cp || "",
      provincia: carSnap.provincia || "",
    } : null);
  const listaCargadores = (carElegido?.nif && !cargadores.some((c) => (c.nif || "").toUpperCase() === carElegido.nif.toUpperCase())
    ? [carElegido, ...cargadores]
    : cargadores
  ).filter((c) => (c.nif || "").trim());

  const payloadTransporte = () => {
    const veh = (mod.vehiculo || "").trim();
    const mats = [veh, (mod.remolque || "").trim()].filter(Boolean);
    const precioRaw = (mod.precio || "").trim();
    let precio = "";
    if (precioRaw) {
      const n = Number(precioRaw.replace(",", "."));
      if (!(n >= 0)) {
        toast.error("Precio del transporte no v" + String.fromCharCode(0xe1) + "lido");
        return null;
      }
      precio = n;
    }
    const body = {
      matriculas: mats,
      conductor: (mod.conductor || "").trim() || null,
      precio_transporte: precio,
      autorizacion_especial: (mod.autorizacion || "").trim(),
      observaciones: mod.observaciones,
      fecha_descarga: (mod.fecha_descarga || "").trim() || "",
    };
    if (traElegido?.nif && traElegido?.razon_social) {
      body.transportista = {
        razon_social: traElegido.razon_social,
        nif: traElegido.nif,
        domicilio: traElegido.domicilio || "",
        municipio: traElegido.municipio || "",
        cp: traElegido.cp || "",
        provincia: traElegido.provincia || "",
        email: traElegido.email || "",
      };
    }
    const nifIni = String(data?.deca?.cargador_nif || data?.deca?.cargador_snapshot?.nif || "").trim().toUpperCase();
    const oriIni = String(data?.envios?.[0]?.origen || "").trim();
    const nifNow = String(carElegido?.nif || "").trim().toUpperCase();
    const oriNow = String(mod.origen || fmtDomicilio(carElegido) || "").trim();
    if (carElegido?.nif && carElegido?.razon_social && (nifNow !== nifIni || oriNow !== oriIni)) {
      body.cargador = {
        razon_social: carElegido.razon_social,
        nif: carElegido.nif,
        domicilio: carElegido.domicilio || "",
        municipio: carElegido.municipio || "",
        cp: carElegido.cp || "",
        provincia: carElegido.provincia || "",
        email: carElegido.email || "",
      };
      if (oriNow) body.origen = oriNow;
    }
    return body;
  };

  const modificar = async () => {
    const veh = (mod.vehiculo || "").trim();
    if (!veh && !esBorrador) {
      toast.error("Indica la matr" + String.fromCharCode(0xed) + "cula del veh" + String.fromCharCode(0xed) + "culo");
      return;
    }
    const motivo = esBorrador ? ((mod.motivo || "").trim() || "Actualizar borrador") : (mod.motivo || "").trim();
    if (motivo.length < MOTIVO_MIN) {
      toast.error(T_MOTIVO);
      return;
    }
    const body = payloadTransporte();
    if (!body) return;
    try {
      await api.post(`/api/v1/decas/${id}/modificar`, {
        ...body,
        motivo,
      });
      toast.success(esBorrador ? "Borrador actualizado" : "DeCA modificado (nueva versi" + String.fromCharCode(0xf3) + "n)");
      setModOpen(false);
      load();
    } catch (e) {       toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const emitir = async () => {
    const veh = (mod.vehiculo || "").trim();
    if (!veh) {
      toast.error("Indica la matr" + String.fromCharCode(0xed) + "cula del veh" + String.fromCharCode(0xed) + "culo");
      return;
    }
    if (!traElegido?.nif || !traElegido?.razon_social) {
      toast.error("Selecciona el transportista");
      return;
    }
    const body = payloadTransporte();
    if (!body) return;
    try {
      await api.post(`/api/v1/decas/${id}/emitir`, body);
      toast.success("DeCA emitido. Ya es un documento oficial.");
      setModOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const anular = async () => {
    const motivo = (anularMotivo || "").trim();
    if (motivo.length < MOTIVO_MIN) {
      toast.error(T_MOTIVO);
      return false;
    }
    try {
      await api.post(`/api/v1/decas/${id}/anular`, { motivo });
      toast.success("DeCA anulado");
      setAnularMotivo("");
      load();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const setEnvioEdit = (idx, patch) => {
    setEditEnvios((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const setDestEdit = (idx, patch) => {
    setEditEnvios((rows) => rows.map((r, i) => (
      i === idx ? { ...r, destinatario: { ...r.destinatario, ...patch } } : r
    )));
  };

  const guardarEnvios = async () => {
    if (!editEnvios.length) {
      toast.error("A" + N + "ade al menos un albar" + A + "n");
      return;
    }
    for (const [i, e] of editEnvios.entries()) {
      if (!e.destinatario?.razon_social?.trim() || !e.destinatario?.nif?.trim()) {
        toast.error("Albar" + A + "n " + (i + 1) + ": indica raz" + O + "n social y NIF del destino");
        return;
      }
      if (!esIdFiscal(e.destinatario.nif)) {
        toast.error("Albar" + A + "n " + (i + 1) + ": identificador fiscal no v" + A + "lido (NIF espa" + N + "ol o VAT extranjero)");
        return;
      }
      if (e.destinatario.cp?.trim() && !esCpIntl(e.destinatario.cp)) {
        toast.error("Albar" + A + "n " + (i + 1) + ": c" + O + "digo postal no v" + A + "lido");
        return;
      }
      if (!esDireccionCompleta(e.origen)) {
        toast.error("Albar" + A + "n " + (i + 1) + ": el origen debe incluir calle, CP, municipio y provincia");
        return;
      }
      if (!String(e.destino || "").trim()) {
        toast.error("Albar" + A + "n " + (i + 1) + ": falta el destino");
        return;
      }
      if (!e.naturaleza?.trim()) {
        toast.error("Albar" + A + "n " + (i + 1) + ": falta la naturaleza");
        return;
      }
      if (!(Number(String(e.peso_bruto).replace(",", ".")) > 0)) {
        toast.error("Albar" + A + "n " + (i + 1) + ": el peso bruto debe ser mayor que 0");
        return;
      }
      if (!(Number(e.bultos) > 0)) {
        toast.error("Albar" + A + "n " + (i + 1) + ": los bultos deben ser mayor que 0");
        return;
      }
    }
    setEnviosBusy(true);
    try {
      await api.put(`/api/v1/decas/${id}/envios`, {
        envios: editEnvios.map((e) => ({
          origen: e.origen,
          destinatario: e.destinatario,
          destino: e.destino,
          naturaleza: e.naturaleza,
          bultos: Number(e.bultos),
          peso_bruto: Number(String(e.peso_bruto).replace(",", ".")),
          albaran_ejercicio: e.albaran_ejercicio ? Number(e.albaran_ejercicio) : null,
          albaran_serie: String(e.albaran_serie || "").trim(),
          albaran_numero: e.albaran_numero,
        })),
      });
      toast.success("Albaranes del borrador actualizados");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setEnviosBusy(false);
    }
  };

  if (!data) return <div className="p-6 text-slate-500">Cargando…</div>;
  const { deca, envios, versiones, puede_editar_envios: puedeEditarEnvios } = data;
  const publicUrl = descargaUrl(deca.download_token);
  const verifyUrl = `${window.location.origin}/verify/${deca.download_token}`;

  return (
    <div>
      <PageHeader
        title="Detalle del DeCA"
        subtitle={`${fmtNumeroDeca(deca)} · versi${String.fromCharCode(0xf3)}n ${deca.version}`}
        testid="page-detalle"
        actions={<>
          <Button variant="outline" onClick={()=>nav("/decas")}><ChevronLeft className="h-4 w-4 mr-2"/> Histórico</Button>
          <a href={decaPdfPath(deca.id)} target="_blank" rel="noreferrer">
            <Button variant="outline" data-testid="btn-pdf"><FileText className="h-4 w-4 mr-2"/> Ver PDF</Button>
          </a>
          <a href={verifyUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" data-testid="btn-verify"><ShieldCheck className="h-4 w-4 mr-2"/> Verificar</Button>
          </a>
          {deca.estado !== "borrador" ? (
            <>
              <BotonEnviarEmail size="default" onClick={()=>setEmailOpen(true)}/>
              <EnviarDecaEmail decaId={deca.id} open={emailOpen} onOpenChange={setEmailOpen}/>
            </>
          ) : (
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={()=>setModOpen(true)} data-testid="btn-emitir">
              <FilePlus2 className="h-4 w-4 mr-2"/> Completar y emitir
            </Button>
          )}
          {deca.estado !== "anulado" ? (
            <Dialog open={modOpen} onOpenChange={setModOpen}>
              <DialogTrigger asChild><Button variant="outline" data-testid="btn-modificar"><Pencil className="h-4 w-4 mr-2"/> Modificar</Button></DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{deca.estado === "borrador" ? "Completar borrador" : "Modificar DeCA"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="mod-cargador" className="label-tag">Cargador *</Label>
                    <Select
                      value={mod.cargador_nif || "none"}
                      onValueChange={(v) => {
                        if (v === "none") {
                          setMod((m) => ({ ...m, cargador_nif: "", orig_id: "cargador", origen: "" }));
                          return;
                        }
                        const c = listaCargadores.find((x) => (x.nif || "").toUpperCase() === v.toUpperCase());
                        setMod((m) => ({
                          ...m,
                          cargador_nif: v,
                          orig_id: "cargador",
                          origen: fmtDomicilio(c) || m.origen,
                        }));
                      }}
                    >
                      <SelectTrigger id="mod-cargador" data-testid="mod-cargador"><SelectValue placeholder="Selecciona cargador"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin cargador</SelectItem>
                        {listaCargadores.map((c) => (
                          <SelectItem key={c.nif} value={c.nif}>{c.razon_social} ({c.nif})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {carElegido?.nif ? (
                      <div className="mt-2">
                        <DestinoClienteSelect
                          modo="cargador"
                          clienteNif={carElegido.nif}
                          clienteDir={carElegido}
                          valueId={mod.orig_id || "cargador"}
                          testid="mod-origen-cargador"
                          onSelect={(sel) => setMod((m) => ({
                            ...m,
                            orig_id: sel.id || "cargador",
                            origen: sel.texto || fmtDomicilio(sel) || m.origen,
                          }))}
                        />
                        <div className="text-[11px] text-slate-500 mt-1">
                          El origen de todos los env{I}os se actualiza al domicilio del cargador (o al almac{E}n elegido) y se genera una versi{O}n nueva.
                        </div>
                      </div>
                    ) : null}
                  </div>
                  {deca.estado === "borrador" ? (
                    <div>
                      <Label htmlFor="mod-transportista" className="label-tag">Transportista *</Label>
                      <Select value={mod.transportista_nif || "none"} onValueChange={(v) => setMod((m) => ({ ...m, transportista_nif: v === "none" ? "" : v }))}>
                        <SelectTrigger id="mod-transportista" data-testid="mod-transportista"><SelectValue placeholder="Selecciona transportista"/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin transportista</SelectItem>
                          {(traElegido?.nif && !transportistas.some((t) => (t.nif || "").toUpperCase() === traElegido.nif.toUpperCase())
                            ? [{ nif: traElegido.nif, razon_social: traElegido.razon_social }, ...transportistas]
                            : transportistas
                          ).map((t) => (
                            <SelectItem key={t.nif} value={t.nif}>{t.razon_social} ({t.nif})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500">
                      {(deca.transportista_snapshot?.razon_social || "Transportista")}
                      {deca.transportista_snapshot?.nif ? ` (${deca.transportista_snapshot.nif})` : ""}
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-3">
                    <MatriculaSelect
                      transportistaNif={traElegido?.nif || deca.transportista_nif || deca.transportista_snapshot?.nif}
                      transportistaRazon={traElegido?.razon_social || deca.transportista_snapshot?.razon_social}
                      transportistaCodigo={traElegido?.codigo || deca.transportista_snapshot?.codigo}
                      tipo="vehiculo"
                      required
                      value={mod.vehiculo}
                      onChange={(v) => setMod((m) => ({ ...m, vehiculo: v }))}
                      testid="mod-vehiculo"
                    />
                    <MatriculaSelect
                      transportistaNif={traElegido?.nif || deca.transportista_nif || deca.transportista_snapshot?.nif}
                      transportistaRazon={traElegido?.razon_social || deca.transportista_snapshot?.razon_social}
                      transportistaCodigo={traElegido?.codigo || deca.transportista_snapshot?.codigo}
                      tipo="remolque"
                      value={mod.remolque}
                      onChange={(v) => setMod((m) => ({ ...m, remolque: v }))}
                      testid="mod-remolque"
                    />
                    <div className="sm:col-span-2">
                      <ConductorSelect
                        transportistaNif={traElegido?.nif || deca.transportista_nif || deca.transportista_snapshot?.nif}
                        transportistaRazon={traElegido?.razon_social || deca.transportista_snapshot?.razon_social}
                        transportistaCodigo={traElegido?.codigo || deca.transportista_snapshot?.codigo}
                        value={mod.conductor}
                        onChange={(v) => setMod((m) => ({ ...m, conductor: v }))}
                        testid="mod-conductor"
                      />
                    </div>
                    <Field label="Precio del transporte">
                      <Input className="mt-1" inputMode="decimal" maxLength={LEN.precio} value={mod.precio} onChange={(e)=>setMod((m)=>({...m, precio:e.target.value}))} placeholder="105,10" data-testid="mod-precio"/>
                    </Field>
                    <Field label={"Autorizaci" + String.fromCharCode(0xf3) + "n especial"}>
                      <Input className="mt-1" maxLength={LEN.autorizacion} value={mod.autorizacion} onChange={(e)=>setMod((m)=>({...m, autorizacion:e.target.value}))} placeholder="ADR / especial" data-testid="mod-autorizacion"/>
                    </Field>
                    <div>
                      <Field label="Fecha de descarga">
                        <DatePicker value={mod.fecha_descarga || ""} onChange={(v) => setMod((m) => ({ ...m, fecha_descarga: v }))} testid="mod-fecha-descarga"/>
                      </Field>
                      <div className="text-[11px] text-slate-500 mt-1">
                        Estimada de fin de transporte. Si est{A} vac{I}a no sale en el PDF.
                      </div>
                    </div>
                  </div>
                  <Field label="Observaciones">
                    <Textarea rows={2} maxLength={LEN.observaciones} value={mod.observaciones} onChange={(e)=>setMod({...mod, observaciones:e.target.value})}/>
                  </Field>
                  <div>
                  {deca.estado !== "borrador" ? (
                    <>
                      <Field label="Motivo *">
                        <Input maxLength={LEN.motivo} value={mod.motivo} onChange={(e)=>setMod({...mod, motivo:e.target.value})} data-testid="mod-motivo" placeholder={T_MOTIVO_PH}/>
                      </Field>
                      <div className="text-[11px] text-slate-500 mt-1">{(mod.motivo || "").trim().length}/{MOTIVO_MIN}</div>
                    </>
                  ) : (
                    <div className="text-xs text-slate-500">Cuando tengas el transportista y la matr{String.fromCharCode(0xed)}cula del veh{String.fromCharCode(0xed)}culo, emite el DeCA. Hasta entonces el albar{String.fromCharCode(0xe1)}n sigue reservado.</div>
                  )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={()=>setModOpen(false)}>Cancelar</Button>
                  {deca.estado === "borrador" ? (
                    <>
                      <Button variant="outline" onClick={modificar} data-testid="btn-guardar-borrador">Guardar borrador</Button>
                      <Button onClick={emitir} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-emitir-save">Emitir DeCA</Button>
                    </>
                  ) : (
                    <Button onClick={modificar} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-modificar-save">Crear nueva versi{String.fromCharCode(0xf3)}n</Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : null}
          {deca.estado !== "anulado" ? (
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="destructive" data-testid="btn-anular"><Ban className="h-4 w-4 mr-2"/> Anular</Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{"Anular este DeCA?"}</AlertDialogTitle>
                  <AlertDialogDescription>Se conserva por trazabilidad, pero queda anulado. El motivo es obligatorio.</AlertDialogDescription>
                </AlertDialogHeader>
                <div>
                  <Field label="Motivo *">
                    <Textarea className="mt-1" rows={3} maxLength={LEN.motivo} value={anularMotivo} onChange={(e)=>setAnularMotivo(e.target.value)} data-testid="anular-motivo" placeholder={T_MOTIVO_PH}/>
                  </Field>
                  <div className="text-[11px] text-slate-500 mt-1">{anularMotivo.trim().length}/{MOTIVO_MIN}</div>
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      if ((anularMotivo || "").trim().length < MOTIVO_MIN) {
                        e.preventDefault();
                        toast.error(T_MOTIVO);
                        return;
                      }
                      anular();
                    }}
                  >Anular</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : null}
        </>}
      />

      {deca.estado === "borrador" ? (
        <div className="mb-4 border border-amber-200 bg-amber-50 rounded-md p-3 text-sm text-amber-950" data-testid="aviso-borrador">
          Este DeCA es un <b>borrador</b>: a{String.fromCharCode(0xfa)}n no es el documento oficial. Completa la matr{String.fromCharCode(0xed)}cula del veh{String.fromCharCode(0xed)}culo y pulsa <b>Completar y emitir</b>. El albar{String.fromCharCode(0xe1)}n queda reservado y no hay QR p{String.fromCharCode(0xfa)}blico hasta entonces.
        </div>
      ) : null}

      <div className="grid xl:grid-cols-[minmax(0,1fr)_360px] gap-4">
        <div className="space-y-4 min-w-0">
          <Card className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="label-tag">DeCA</div>
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>Documento electrónico de Control Administrativo</h2>
                <div className="text-sm text-slate-900 mt-1 font-mono font-semibold">{fmtNumeroDeca(deca)}</div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <StatusBadge estado={deca.estado}/>
                <div className="text-xs text-slate-500">v{deca.version}</div>
                <ExpiryCountdown until={deca.download_disabled_at} estado={deca.estado}/>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3 mt-4">
              <div className="border border-slate-200 rounded-md p-3">
                <div className="label-tag flex items-center gap-1"><Truck className="h-3 w-3"/> Cargador contractual</div>
                <div className="mt-1 font-semibold text-slate-900">{deca.cargador_snapshot.razon_social}</div>
                <div className="text-xs text-slate-600 font-mono">NIF {deca.cargador_snapshot.nif}</div>
                <div className="text-xs text-slate-500 mt-1">{deca.cargador_snapshot.domicilio}</div>
              </div>
              <div className="border border-slate-200 rounded-md p-3">
                <div className="label-tag flex items-center gap-1"><User className="h-3 w-3"/> Transportista efectivo</div>
                <div className="mt-1 font-semibold text-slate-900">{deca.transportista_snapshot.razon_social}</div>
                <div className="text-xs text-slate-600 font-mono">NIF {deca.transportista_snapshot.nif}</div>
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-3 mt-3">
              <KV label="Fecha transporte" value={fmtFecha(deca.fecha_transporte)}/>
              {deca.fecha_descarga ? <KV label="Fecha de descarga" value={fmtFecha(deca.fecha_descarga)}/> : null}
              <KV label={"Matr" + String.fromCharCode(0xed) + "culas"} value={(deca.matriculas || []).join(", ")} mono/>
              <KV label="Conductor" value={deca.conductor || "\u2014"}/>
              <KV label="Precio transporte" value={deca.precio_transporte != null && deca.precio_transporte !== "" ? `${deca.precio_transporte} \u20ac` : "\u2014"}/>
              <KV label={"Autorizaci" + String.fromCharCode(0xf3) + "n"} value={deca.autorizacion_especial || "\u2014"}/>
              <KV label={palabraEnvio(deca.num_envios).replace(/^./, (c) => c.toUpperCase())} value={deca.num_envios}/>
            </div>
            {deca.observaciones ? (
              <div className="mt-3">
                <div className="label-tag">Observaciones</div>
                <div className="text-sm text-slate-800 mt-1">{deca.observaciones}</div>
              </div>
            ) : null}
          </Card>

          <Card className="overflow-hidden">
            <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
              <div className="label-tag mb-0">{Number((puedeEditarEnvios ? editEnvios : envios).length) === 1 ? "Env" + I + "o incluido" : "Env" + I + "os incluidos"} ({(puedeEditarEnvios ? editEnvios : envios).length})</div>
              {puedeEditarEnvios ? (
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => setEditEnvios((rows) => [...rows, emptyEnvioEdit(user?.naturaleza_por_defecto, user?.bultos_defecto_activo && user?.bultos_defecto > 0 ? String(user.bultos_defecto) : "")])} data-testid="btn-add-envio-borrador">
                    <Plus className="h-3.5 w-3.5 mr-1"/> A{N}adir albar{A}n
                  </Button>
                  <Button type="button" size="sm" className="bg-blue-700 hover:bg-blue-800" onClick={guardarEnvios} disabled={enviosBusy} data-testid="btn-save-envios-borrador">
                    {enviosBusy ? "Guardando" + String.fromCharCode(0x2026) : "Guardar albaranes"}
                  </Button>
                </div>
              ) : null}
            </div>
            {puedeEditarEnvios ? (
              <div className="p-3 space-y-4">
                {editEnvios.map((e, idx) => (
                  <div key={e.key} className="border border-slate-200 rounded-md p-3" data-testid={`envio-edit-${idx}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo" }}>Albar{A}n {idx + 1}</div>
                      {editEnvios.length > 1 ? (
                        <Button type="button" variant="ghost" size="sm" className="text-red-700" onClick={() => setEditEnvios((rows) => rows.filter((_, i) => i !== idx))}>
                          <Trash2 className="h-3.5 w-3.5 mr-1"/> Quitar
                        </Button>
                      ) : null}
                    </div>
                    <div className="grid lg:grid-cols-2 gap-3">
                      <Field label={"Raz" + O + "n social destino *"}>
                        <Input className="mt-1" maxLength={LEN.razonSocial} value={e.destinatario.razon_social} onChange={(ev) => setDestEdit(idx, { razon_social: ev.target.value })}/>
                      </Field>
                      <Field label="NIF destino *">
                        <Input className="mt-1 font-mono uppercase" maxLength={LEN.nif} value={e.destinatario.nif} onChange={(ev) => setDestEdit(idx, { nif: ev.target.value })}/>
                      </Field>
                      <Field className="lg:col-span-2" label={"Destino (direcci" + O + "n)"}>
                        <Input className="mt-1" maxLength={LEN.domicilio} value={e.destino} onChange={(ev) => setEnvioEdit(idx, { destino: ev.target.value })}/>
                      </Field>
                      <Field className="lg:col-span-2" label="Origen (calle, CP, municipio y provincia)">
                        <Input className="mt-1" maxLength={LEN.domicilio} value={e.origen} onChange={(ev) => setEnvioEdit(idx, { origen: ev.target.value })}/>
                      </Field>
                      <Field className="lg:col-span-2" label={"Naturaleza de la mercanc" + I + "a"}>
                        <Input className="mt-1" maxLength={LEN.naturaleza} value={e.naturaleza} onChange={(ev) => setEnvioEdit(idx, { naturaleza: ev.target.value })}/>
                      </Field>
                      <Field label="Bultos">
                        <Input className="mt-1" inputMode="decimal" maxLength={8} value={e.bultos} onChange={(ev) => setEnvioEdit(idx, { bultos: ev.target.value })}/>
                      </Field>
                      <Field label="Peso bruto (kg)">
                        <Input className="mt-1" inputMode="decimal" maxLength={LEN.precio} value={e.peso_bruto} onChange={(ev) => setEnvioEdit(idx, { peso_bruto: ev.target.value })}/>
                      </Field>
                      <Field label="Ejercicio">
                        <Input className="mt-1" inputMode="numeric" maxLength={4} value={e.albaran_ejercicio} onChange={(ev) => setEnvioEdit(idx, { albaran_ejercicio: ev.target.value })}/>
                      </Field>
                      <Field label="Serie">
                        <Input className="mt-1" maxLength={LEN.serie} value={e.albaran_serie} onChange={(ev) => setEnvioEdit(idx, { albaran_serie: ev.target.value })}/>
                      </Field>
                      <Field label={"N" + String.fromCharCode(0xba) + " albar" + A + "n"}>
                        <Input className="mt-1" inputMode="numeric" maxLength={12} value={e.albaran_numero} onChange={(ev) => setEnvioEdit(idx, { albaran_numero: ev.target.value })}/>
                      </Field>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <EnviosSortableTable
                  envios={envios}
                  destinoLabel="Destinatario / destino"
                  className="deca-table w-full min-w-[720px]"
                />
              </div>
            )}
          </Card>

          {versiones?.length > 1 ? (
            <Card className="p-4">
              <div className="label-tag mb-2">Historial de versiones</div>
              <div className="grid gap-2">
                {versiones.map((v) => (
                  <div key={v.id} className="text-sm flex justify-between gap-4 border border-slate-200 rounded px-3 py-2">
                    <div className="min-w-0">
                      <div>Versi{O}n <b>v{v.version}</b></div>
                      {v.motivo ? (
                        <div className="text-xs text-slate-600 mt-0.5" data-testid={`version-motivo-${v.version}`}>
                          <span className="text-slate-400 uppercase tracking-wider text-[10px] mr-1">Motivo</span>
                          {v.motivo}
                        </div>
                      ) : null}
                    </div>
                    <div className="text-slate-500 font-mono text-xs shrink-0">{fmtFechaHora(v.created_at)}</div>
                  </div>
                ))}
                {deca.motivo_anulacion ? (
                  <div className="text-sm border border-red-200 bg-red-50 rounded px-3 py-2 text-red-900">
                    <div className="text-[10px] uppercase tracking-wider text-red-500">Motivo de anulaci{O}n</div>
                    <div className="text-xs mt-0.5">{deca.motivo_anulacion}</div>
                  </div>
                ) : null}
              </div>
            </Card>
          ) : null}
        </div>

        <div className="space-y-4 min-w-0 overflow-hidden">
          <Card className="p-5 text-center">
            {isPublicDownloadActive(deca) ? (
              <>
                <div className="label-tag mb-3">Escanea para descargar</div>
                <div className="flex justify-center"><QrDisplay value={publicUrl} size={240} testid="qr-detalle"/></div>
                <div className="mt-3 text-xs text-slate-500">URL directa · sin autenticaci{String.fromCharCode(0xf3)}n · L1–L3</div>
              </>
            ) : (
              <div className="text-left border border-amber-200 bg-amber-50 rounded-md p-3" data-testid="qr-detalle-caducado">
                <div className="label-tag text-amber-800">Descarga p{String.fromCharCode(0xfa)}blica</div>
                <div className="mt-1 text-sm font-semibold text-amber-950">
                  {deca.estado === "anulado" ? "DeCA anulado" : deca.estado === "borrador" ? "Borrador: sin descarga p" + String.fromCharCode(0xfa) + "blica" : "Descarga caducada"}
                </div>
                <div className="mt-1 text-xs text-amber-900">
                  El QR p{String.fromCharCode(0xfa)}blico ya no es v{String.fromCharCode(0xe1)}lido. El inspector ver{String.fromCharCode(0xe1)} una p{String.fromCharCode(0xe1)}gina explicativa, no el PDF.
                </div>
              </div>
            )}
            {(envios || []).some((e) => e.firma_token) ? (
              <div className="mt-3 text-left space-y-1.5">
                <div className="label-tag">Firma de recepción por albarán</div>
                {envios.filter((e) => e.firma_token).map((e) => {
                  const nro = fmtAlbaranNro(e.albaran_serie, e.albaran_numero);
                  return e.firmado_at ? (
                    <div key={e.id} className="flex items-center justify-between gap-2 border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2">
                      <span className="text-sm">Albarán {nro}</span>
                      <span className="text-[10px] text-emerald-800">Firmado{e.firmado_por ? ` · ${e.firmado_por}` : ""}</span>
                    </div>
                  ) : (
                    <a key={e.id} href={firmarUrl(e.firma_token)} target="_blank" rel="noreferrer" className="block">
                      <Button variant="outline" size="sm" className="w-full justify-between" data-testid={`btn-firmar-${e.id}`}>
                        <span>Albarán {nro}</span>
                        <span className="text-[10px] text-slate-500 truncate max-w-[55%]">{e.destinatario_nombre}</span>
                      </Button>
                    </a>
                  );
                })}
              </div>
            ) : null}
          </Card>
          <Card className="p-4 min-w-0 overflow-hidden">
            <DecaNotes decaId={deca.id}/>
          </Card>
          <Card className="p-4">
            <div className="label-tag mb-1">Emitido por</div>
            <div className="text-sm font-medium text-slate-900">{deca.created_by_email}</div>
            <div className="text-xs text-slate-500 mt-2">Creado: {fmtFechaHora(deca.created_at)}</div>
            <div className="text-xs text-slate-500">Actualizado: {fmtFechaHora(deca.updated_at)}</div>
            <div className="text-xs text-slate-500 mt-2">Tamaño PDF: {(deca.pdf_size_bytes/1024).toFixed(1)} KB</div>
          </Card>
        </div>
      </div>
    </div>
  );
}
